import type { CatalogEntry } from '../games/catalog';
import { useGuide } from '../lib/lazyGuides';
import { Link } from 'react-router-dom';
import { categoryListing } from '../games/listings';
import { gameUpdated } from '../games/gameDates';
import { formatDay } from '../games/fresh';
import { articlesFor } from '../articles';
import { shortCount, useGameStats } from '../lib/gameStats';

/**
 * About / What's inside / How to play / Tips / Strategy / FAQ under a game's Play screen.
 * scripts/prerender.mjs writes the same text into the page file.
 */
export function GameGuide({ entry }: { entry: CatalogEntry }) {
  const guide = useGuide(entry.id);
  const { plays, likes } = useGameStats(entry.id);
  const articles = articlesFor(entry.id);
  return (
    <>
    <nav className="crumbs" aria-label="Breadcrumb">
      <ol>
        <li>
          <Link to="/">Games</Link>
        </li>
        <li>
          <Link to={`/games/${categoryListing(entry.category)?.slug ?? ''}`}>{entry.categoryLabel}</Link>
        </li>
        <li aria-current="page">{entry.name}</li>
      </ol>
    </nav>
    <section className="guide" aria-label={`About ${entry.name}`}>
      {guide && (
        <div className="guide__text">
          <p className="guide__facts">
            <span>Updated {formatDay(gameUpdated(entry.id))}</span>
            {plays > 0 && <span>{shortCount(plays)} plays</span>}
            {likes > 0 && <span>{shortCount(likes)} likes</span>}
            <span>Made by SuperFun</span>
          </p>
          <h2>About {entry.name}</h2>
          <p>{guide.about}</p>
          {guide.more && <p className="guide__more">{guide.more}</p>}
          {guide.features && (
            <>
              <h2>What's inside</h2>
              <ul>
                {guide.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            </>
          )}
          <h2>How to play</h2>
          <ul>
            {guide.controls.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
          <h2>Tips</h2>
          <ul>
            {guide.tips.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
          {guide.strategy && (
            <>
              <h2>Strategy guide</h2>
              {guide.strategy.map((p) => (
                <p key={p} className="guide__para">
                  {p}
                </p>
              ))}
            </>
          )}
          {guide.faq && (
            <>
              <h2>Questions and answers</h2>
              <dl className="guide__faq">
                {guide.faq.map(({ q, a }) => (
                  <div key={q}>
                    <dt>{q}</dt>
                    <dd>{a}</dd>
                  </div>
                ))}
              </dl>
            </>
          )}
          {articles.length > 0 && (
            <>
              <h2>Read more</h2>
              <ul>
                {articles.map((a) => (
                  <li key={a.slug}>
                    <Link to={`/guides/${a.slug}`}>{a.title}</Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
    </>
  );
}
